import { computed } from 'vue'

import { KANBAN_LOG_EMPTY_VALUE } from '@entities/kanban-comment'
import { useListsStore } from '@shared/model'

interface KanbanLogExtraFieldsPayload {
  oldValue: string | null
  value: string | null
}

interface ExtraFieldEntry {
  label: string
  value: string
}

interface KanbanLogExtraFieldsReturn {
  refOldEntries: ComputedRef<ExtraFieldEntry[]>
  refNewEntries: ComputedRef<ExtraFieldEntry[]>
}

const formatKey = (key: string): string => {
  const spaced = key.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const extraFieldsValuesList = (): Record<string, string> | null => {
  const listsStore = useListsStore()

  if (!listsStore.getExtraFieldsLists) return null
  return Object.assign({}, ...Object.values(listsStore.getExtraFieldsLists))
}

const labelFor = (value: unknown, list: Record<string, string> | null): string =>
  list?.[String(value)] || String(value)

/** A field may hold a list — CS can raise a request with several reasons at once. */
const formatValue = (value: unknown, list: Record<string, string> | null): string => {
  if (!Array.isArray(value)) return labelFor(value, list)
  if (!value.length) return KANBAN_LOG_EMPTY_VALUE

  return value.map(entry => labelFor(entry, list)).join(', ')
}

const parseEntries = (raw: string | null): ExtraFieldEntry[] => {
  if (!raw) return []

  try {
    const parsed = JSON.parse(raw)
    const list = extraFieldsValuesList()
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return []

    return Object.entries(parsed as Record<string, unknown>).map(([key, value]) => ({
      label: formatKey(key),
      value: formatValue(value, list),
    }))
  } catch {
    return []
  }
}

export const useKanbanLogExtraFields = (
  payload: KanbanLogExtraFieldsPayload,
): KanbanLogExtraFieldsReturn => {
  const refOldEntries = computed<ExtraFieldEntry[]>(() => parseEntries(payload.oldValue))
  const refNewEntries = computed<ExtraFieldEntry[]>(() => parseEntries(payload.value))

  return {
    refOldEntries,
    refNewEntries,
  }
}